/**
 * "वोट डाला" on the phone (N-PAC, 31 Oct 2026): what the booth agent's screen needs that is not React —
 * reading the voter slip's QR code ("NV1|<assembly>|<booth>|<serial>"), a typed serial number, and the
 * small queue of marks not yet sent (kept on the phone so a mark is never lost when the network drops).
 *
 * Plain TypeScript, no browser globals: unit-tested in Node.
 */
import { normalizeDigits } from "@core/boothCode";

export type MarkVia = "serial" | "qr" | "list";

export interface QueuedMark {
  serial: number;
  via: MarkVia;
  /** device time it was marked */
  at: number;
}

export type SlipRead =
  | { ok: true; serial: number }
  | { ok: false; reason: "not_slip" }
  | { ok: false; reason: "other_assembly"; assembly: string }
  | { ok: false; reason: "other_booth"; booth: string };

/** The voter slip's QR code, checked against the open assembly and booth. */
export function readSlipQr(text: string, assemblyId: string, boothCode: string): SlipRead {
  const parts = text.trim().split("|");
  if (parts.length !== 4 || parts[0] !== "NV1") return { ok: false, reason: "not_slip" };
  const [, aid, booth, s] = parts;
  const serial = /^[0-9]{1,6}$/.test(s) ? Number(s) : NaN;
  if (!aid || !booth || !Number.isInteger(serial) || serial < 1 || serial > 100000) return { ok: false, reason: "not_slip" };
  if (aid.toUpperCase() !== assemblyId.toUpperCase()) return { ok: false, reason: "other_assembly", assembly: aid };
  if (booth.toUpperCase() !== boothCode.toUpperCase()) return { ok: false, reason: "other_booth", booth };
  return { ok: true, serial };
}

/** A typed serial number ("45", "०४५", " 45 "): 1–100000, else null. */
export function parseSerial(input: string): number | null {
  const s = normalizeDigits(input).trim();
  if (!/^[0-9]{1,6}$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= 100000 ? n : null;
}

/** where the unsent marks of one booth are kept */
export const queueKey = (assemblyId: string, boothCode: string) => `votedQueue:${assemblyId}:${boothCode}`;

/** The stored queue; anything unreadable is an empty queue (never throws). */
export function readQueue(raw: string | null): QueuedMark[] {
  if (!raw) return [];
  try {
    const v = JSON.parse(raw) as unknown;
    if (!Array.isArray(v)) return [];
    return v
      .filter((x): x is QueuedMark => !!x && typeof x === "object" && Number.isInteger((x as QueuedMark).serial) && (x as QueuedMark).serial > 0)
      .map((x) => ({ serial: x.serial, via: x.via === "qr" || x.via === "list" ? x.via : "serial", at: Number(x.at) || 0 }));
  } catch {
    return [];
  }
}

/** Add a mark; the same serial twice stays once (the first one). */
export function addMark(queue: readonly QueuedMark[], m: QueuedMark): QueuedMark[] {
  return queue.some((q) => q.serial === m.serial) ? [...queue] : [...queue, m];
}

/** Drop the marks the server has answered for (marked, already marked, or not in the list). */
export function dropSent(queue: readonly QueuedMark[], serials: readonly number[]): QueuedMark[] {
  const done = new Set(serials);
  return queue.filter((q) => !done.has(q.serial));
}

/** at most this many marks per request (the API takes 500) */
export const SEND_BATCH = 200;

/** a code read by the camera counts again only after 3 seconds (the same slip stays in view) */
export function freshScan(last: { text: string; at: number } | null, text: string, now: number, gapMs = 3000): boolean {
  return !last || last.text !== text || now - last.at >= gapMs;
}
